import React, { useState, useEffect, useCallback } from 'react';
import { useEntity } from '../contexts/EntityContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Calculator, ChevronDown, ChevronUp, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

export function VATReturns() {
  const { selectedEntity, isDutchEntity } = useEntity();
  const { language } = useLanguage();
  const [vatReturns, setVatReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedQuarter, setSelectedQuarter] = useState(getCurrentQuarter());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [expandedReturn, setExpandedReturn] = useState(null);

  function getCurrentQuarter() {
    return Math.ceil((new Date().getMonth() + 1) / 3);
  }

  const fetchVATReturns = useCallback(async () => {
    if (!selectedEntity) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/vat-returns?entity_id=${selectedEntity.entity_id}`, { credentials: 'include' });
      if (res.ok) setVatReturns(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedEntity]);

  useEffect(() => { fetchVATReturns(); }, [fetchVATReturns]);

  const generateVATReturn = async () => {
    setGenerating(true);
    try {
      const res = await fetch(
        `${API_URL}/api/vat-returns/generate?entity_id=${selectedEntity.entity_id}&quarter=${selectedQuarter}&year=${selectedYear}`,
        { method: 'POST', credentials: 'include' }
      );
      if (res.ok) {
        toast.success(`BTW-aangifte Q${selectedQuarter} ${selectedYear} gegenereerd`);
        fetchVATReturns();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fout bij genereren');
      }
    } catch (e) { toast.error('Fout bij genereren'); }
    finally { setGenerating(false); }
  };

  const eur = (amount) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount || 0);

  const quarterNames = ['Q1 (jan-mrt)', 'Q2 (apr-jun)', 'Q3 (jul-sep)', 'Q4 (okt-dec)'];

  if (!isDutchEntity) {
    return (
      <Card><CardContent className="py-12 text-center">
        <p className="text-muted-foreground">BTW-aangifte is alleen beschikbaar voor Nederlandse entiteiten. Zie Belastinginstructies voor Amerikaanse entiteiten.</p>
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="vat-returns-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          BTW-aangifte
        </h1>
        <p className="text-muted-foreground text-sm mt-1">{selectedEntity?.name}</p>
      </div>

      {/* Generate */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Calculator className="w-4 h-4" /> Aangifte genereren
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Kwartaal</label>
              <Select value={selectedQuarter.toString()} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
                <SelectTrigger className="w-48" data-testid="quarter-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4].map(q => (<SelectItem key={q} value={q.toString()}>{quarterNames[q - 1]}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Jaar</label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-32" data-testid="year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map(y => (<SelectItem key={y} value={y.toString()}>{y}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={generateVATReturn} disabled={generating} className="btn-brand" data-testid="generate-vat-btn">
              {generating ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
              Genereer aangifte
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Op basis van je journaalposten met BTW-regels (rekening 1600 en 1700) in het geselecteerde kwartaal.
          </p>
        </CardContent>
      </Card>

      {/* VAT Returns List */}
      {loading ? (
        <div className="py-8 text-center"><div className="w-8 h-8 border-2 border-[hsl(var(--brand))] border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : vatReturns.length > 0 ? (
        <div className="space-y-4">
          {vatReturns.map(vr => (
            <VATReturnCard
              key={vr.return_id}
              vr={vr}
              quarterNames={quarterNames}
              eur={eur}
              expanded={expandedReturn === vr.return_id}
              onToggle={() => setExpandedReturn(expandedReturn === vr.return_id ? null : vr.return_id)}
            />
          ))}
        </div>
      ) : (
        <Card><CardContent className="py-12 text-center">
          <Calculator className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Nog geen BTW-aangiftes. Genereer je eerste aangifte hierboven.</p>
        </CardContent></Card>
      )}

      {/* Info card */}
      <Card className="border-dashed">
        <CardContent className="py-5">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Indienen bij de Belastingdienst</h3>
              <p className="text-xs text-muted-foreground">
                Gebruik de berekende bedragen om je aangifte in te vullen op Mijn Belastingdienst Zakelijk. Directe elektronische indiening vereist een SBR-certificaat.
              </p>
              <a href="https://mijn.belastingdienst.nl" target="_blank" rel="noopener noreferrer"
                className="text-xs text-[hsl(var(--brand))] hover:underline mt-1 inline-block">
                Naar Mijn Belastingdienst &rarr;
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function VATReturnCard({ vr, quarterNames, eur, expanded, onToggle }) {
  const hasDetails = vr.details && vr.details.length > 0;
  const statusBadge = vr.status === 'submitted'
    ? <Badge className="bg-emerald-100 text-emerald-700 text-xs">Ingediend</Badge>
    : vr.status === 'paid'
    ? <Badge className="bg-blue-100 text-blue-700 text-xs">Betaald</Badge>
    : <Badge className="bg-amber-100 text-amber-700 text-xs">Concept</Badge>;

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API_URL}/api/vat-returns/${vr.return_id}/pdf`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `BTW_Q${vr.quarter}_${vr.year}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('PDF download mislukt'); }
  };

  return (
    <Card data-testid={`vat-return-${vr.return_id}`}>
      <CardContent className="py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {quarterNames[(vr.quarter || 1) - 1]} {vr.year}
            </h3>
            {statusBadge}
            <span className="text-xs text-muted-foreground">{vr.entry_count || 0} boekingen</span>
          </div>
          <div className={`text-lg font-bold tabular-nums ${vr.vat_due >= 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
            {vr.vat_due >= 0 ? 'Te betalen: ' : 'Terug te vragen: '}{eur(Math.abs(vr.vat_due))}
          </div>
        </div>

        {/* Rubrieken overview */}
        <div className="bg-muted/40 rounded-lg p-4 mb-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th className="text-left py-1 w-12">Nr.</th>
                <th className="text-left py-1">Rubriek</th>
                <th className="text-right py-1">Omzet</th>
                <th className="text-right py-1">BTW</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-dashed" data-testid="rubriek-1a">
                <td className="py-1.5 text-xs text-muted-foreground">1a</td>
                <td className="py-1.5">Prestaties belast met hoog tarief (21%)</td>
                <td className="text-right py-1.5 tabular-nums">{eur(vr.revenue_high)}</td>
                <td className="text-right py-1.5 tabular-nums font-medium">{eur(vr.vat_high)}</td>
              </tr>
              <tr className="border-b border-dashed" data-testid="rubriek-1b">
                <td className="py-1.5 text-xs text-muted-foreground">1b</td>
                <td className="py-1.5">Prestaties belast met laag tarief (9%)</td>
                <td className="text-right py-1.5 tabular-nums">{eur(vr.revenue_low)}</td>
                <td className="text-right py-1.5 tabular-nums font-medium">{eur(vr.vat_low)}</td>
              </tr>
              <tr className="border-b" data-testid="rubriek-1e">
                <td className="py-1.5 text-xs text-muted-foreground">1e</td>
                <td className="py-1.5">Prestaties belast met 0% / vrijgesteld</td>
                <td className="text-right py-1.5 tabular-nums">{eur(vr.revenue_other)}</td>
                <td className="text-right py-1.5 tabular-nums">-</td>
              </tr>
            </tbody>
          </table>

          <div className="border-t mt-2 pt-2">
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-dashed">
                  <td className="py-1.5 text-xs text-muted-foreground w-12">5a/b</td>
                  <td className="py-1.5">Verschuldigde BTW</td>
                  <td className="text-right py-1.5 tabular-nums font-medium">{eur(vr.vat_collected)}</td>
                </tr>
                <tr className="border-b border-dashed">
                  <td className="py-1.5 text-xs text-muted-foreground">5c</td>
                  <td className="py-1.5">Voorbelasting (aftrek)</td>
                  <td className="text-right py-1.5 tabular-nums font-medium text-emerald-600">{eur(vr.vat_paid)}</td>
                </tr>
                <tr className="font-bold border-t-2">
                  <td className="py-2 text-xs text-muted-foreground">5f</td>
                  <td className="py-2">{vr.vat_due >= 0 ? 'Te betalen' : 'Terug te vragen'}</td>
                  <td className={`text-right py-2 tabular-nums ${vr.vat_due >= 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {eur(Math.abs(vr.vat_due))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          {hasDetails ? (
            <button onClick={onToggle} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="toggle-details">
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? 'Verberg details' : `Toon details (${vr.details.length} regels)`}
            </button>
          ) : <span />}
          <Button variant="outline" size="sm" onClick={downloadPdf} data-testid={`download-vat-pdf-${vr.return_id}`}>
            <Download className="w-3 h-3 mr-1" /> PDF
          </Button>
        </div>
        {hasDetails && expanded && (
          <div className="mt-3 border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left py-2 px-3">Datum</th>
                  <th className="text-left py-2 px-3">Omschrijving</th>
                  <th className="text-left py-2 px-3">Type</th>
                  <th className="text-right py-2 px-3">Netto</th>
                  <th className="text-right py-2 px-3">BTW</th>
                  <th className="text-right py-2 px-3">Tarief</th>
                </tr>
              </thead>
              <tbody>
                {vr.details.map((d, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-1.5 px-3 tabular-nums">{d.date}</td>
                    <td className="py-1.5 px-3 truncate max-w-[200px]">{d.description}</td>
                    <td className="py-1.5 px-3">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${d.type === 'omzet' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                        {d.type === 'omzet' ? 'Omzet' : 'Inkoop'}
                      </span>
                    </td>
                    <td className="text-right py-1.5 px-3 tabular-nums">{eur(d.net)}</td>
                    <td className="text-right py-1.5 px-3 tabular-nums">{eur(d.vat)}</td>
                    <td className="text-right py-1.5 px-3 tabular-nums">{d.rate || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
